/**
 * TriggersPanel (P3-E Doc 40) — Side panel list + manage triggers cho 1 workflow.
 */
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Copy,
  Globe,
  Loader2,
  PenLine,
  Plus,
  Power,
  RefreshCw,
  Trash2,
  Webhook,
  X,
} from "lucide-react";
import * as React from "react";

import {
  triggersApi,
  type WorkflowTrigger,
  type WebhookSecretReveal,
} from "@/api/workflowTriggers";
import { TriggerEditDialog } from "@/components/workflow/TriggerEditDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/ui";

interface Props {
  workflowCode: string;
  onClose: () => void;
}

export function TriggersPanel({ workflowCode, onClose }: Props): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [items, setItems] = React.useState<WorkflowTrigger[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<WorkflowTrigger | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [reveal, setReveal] = React.useState<WebhookSecretReveal | null>(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await triggersApi.list(workflowCode);
      setItems(r.items);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tải được triggers",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [workflowCode, pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleToggle = async (t: WorkflowTrigger): Promise<void> => {
    setBusyId(t.id);
    try {
      await triggersApi.update(t.id, { enabled: !t.enabled });
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không đổi được trạng thái",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (t: WorkflowTrigger): Promise<void> => {
    if (!confirm(`Xoá trigger "${t.name}"?`)) return;
    setBusyId(t.id);
    try {
      await triggersApi.delete(t.id);
      pushToast({ variant: "success", title: "Đã xoá" });
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Xoá thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleTestFire = async (t: WorkflowTrigger): Promise<void> => {
    setBusyId(t.id);
    try {
      const res = await triggersApi.testFire(t.id);
      pushToast({
        variant: res.ok ? "success" : "destructive",
        title: res.ok ? "Test fire OK" : "Test fire failed",
        description: res.run_id
          ? `Run ID: ${res.run_id.slice(0, 8)}... · ${res.status}`
          : res.error ?? "(no detail)",
      });
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Test fire thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleRegenerateSecret = async (
    t: WorkflowTrigger,
  ): Promise<void> => {
    if (
      !confirm(
        `Regenerate HMAC secret cho ${t.slug}? Secret cũ sẽ invalid ngay.`,
      )
    ) {
      return;
    }
    setBusyId(t.id);
    try {
      const r = await triggersApi.regenerateSecret(t.id);
      setReveal(r);
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Regenerate thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusyId(null);
    }
  };

  const copyToClipboard = async (text: string, label: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      pushToast({ variant: "success", title: `Đã copy ${label}` });
    } catch {
      pushToast({ variant: "destructive", title: "Copy thất bại" });
    }
  };

  return (
    <>
      <aside className="w-96 shrink-0 border-l bg-background overflow-y-auto flex flex-col">
        <header className="flex items-center justify-between px-3 py-2 border-b sticky top-0 bg-background z-10">
          <h2 className="text-xs font-semibold uppercase">Triggers</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="p-3 space-y-2">
          <Button
            variant="brand"
            className="w-full"
            size="sm"
            onClick={() => {
              setEditing(null);
              setAdding(true);
            }}
          >
            <Plus className="size-4 mr-1" /> Thêm trigger
          </Button>

          {loading ? (
            <div className="text-xs text-muted-foreground text-center py-6">
              Đang tải...
            </div>
          ) : items.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-6">
              Chưa có trigger nào. Thêm webhook để chạy workflow từ external
              event hoặc cron để chạy theo lịch.
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((t) => (
                <TriggerCard
                  key={t.id}
                  trigger={t}
                  busy={busyId === t.id}
                  onToggle={() => void handleToggle(t)}
                  onTestFire={() => void handleTestFire(t)}
                  onRegenerate={() => void handleRegenerateSecret(t)}
                  onCopyUrl={() =>
                    void copyToClipboard(
                      window.location.origin + (t.webhook_url ?? ""),
                      "URL",
                    )
                  }
                  onEdit={() => {
                    setEditing(t);
                    setAdding(true);
                  }}
                  onDelete={() => void handleDelete(t)}
                />
              ))}
            </div>
          )}
        </div>
      </aside>

      <TriggerEditDialog
        open={adding}
        workflowCode={workflowCode}
        initial={editing}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={async (revealed) => {
          if (revealed) setReveal(revealed);
          await refresh();
        }}
      />

      {reveal ? (
        <SecretRevealDialog
          reveal={reveal}
          onClose={() => setReveal(null)}
          onCopy={(text, label) => void copyToClipboard(text, label)}
        />
      ) : null}
    </>
  );
}

interface CardProps {
  trigger: WorkflowTrigger;
  busy: boolean;
  onToggle: () => void;
  onTestFire: () => void;
  onRegenerate: () => void;
  onCopyUrl: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function TriggerCard({
  trigger,
  busy,
  onToggle,
  onTestFire,
  onRegenerate,
  onCopyUrl,
  onEdit,
  onDelete,
}: CardProps): JSX.Element {
  const isWebhook = trigger.trigger_type === "webhook";
  const Icon = isWebhook ? Webhook : Clock;
  return (
    <div
      className={cn(
        "rounded-md border bg-background p-3 space-y-2",
        !trigger.enabled && "opacity-60",
        busy && "ring-1 ring-primary",
      )}
    >
      <div className="flex items-start gap-2">
        <Icon
          className={cn(
            "size-4 shrink-0 mt-0.5",
            isWebhook ? "text-violet-600" : "text-blue-600",
          )}
        />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-xs truncate">{trigger.name}</div>
          <code className="text-[10px] text-muted-foreground">
            {trigger.slug}
          </code>
        </div>
        {trigger.enabled ? (
          <Badge className="bg-emerald-500 hover:bg-emerald-600 text-white text-[9px] px-1.5 py-0 h-4">
            ON
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4">
            OFF
          </Badge>
        )}
      </div>

      {isWebhook ? (
        <div className="text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-1 truncate flex items-center gap-1">
          <Globe className="size-3" />
          <span className="truncate flex-1">{trigger.webhook_url}</span>
        </div>
      ) : (
        <div className="text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-1 flex items-center gap-1">
          <Clock className="size-3" />
          {trigger.cron_human ?? "(no schedule)"}
        </div>
      )}

      {/* Stats */}
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
        <span>{trigger.fire_count} fires</span>
        {trigger.last_fired_at ? (
          <>
            <span>·</span>
            <span>
              Last:{" "}
              {new Date(trigger.last_fired_at).toLocaleString("vi-VN", {
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </>
        ) : null}
      </div>

      {/* Last error */}
      {trigger.last_error ? (
        <div className="text-[10px] text-destructive bg-destructive/5 border border-destructive/20 rounded px-2 py-1 flex items-start gap-1">
          <AlertTriangle className="size-3 mt-0.5 shrink-0" />
          <span className="line-clamp-2">{trigger.last_error}</span>
        </div>
      ) : null}

      {/* Actions */}
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          className="size-7 p-0"
          onClick={onToggle}
          disabled={busy}
          title={trigger.enabled ? "Disable" : "Enable"}
        >
          <Power className="size-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="size-7 p-0"
          onClick={onTestFire}
          disabled={busy}
          title="Test fire"
        >
          {busy ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <CheckCircle2 className="size-3.5" />
          )}
        </Button>
        {isWebhook ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              className="size-7 p-0"
              onClick={onCopyUrl}
              title="Copy URL"
            >
              <Copy className="size-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="size-7 p-0"
              onClick={onRegenerate}
              disabled={busy}
              title="Regenerate secret"
            >
              <RefreshCw className="size-3.5" />
            </Button>
          </>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          className="size-7 p-0"
          onClick={onEdit}
          title="Sửa"
        >
          <PenLine className="size-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="size-7 p-0 text-muted-foreground hover:text-destructive ml-auto"
          onClick={onDelete}
          disabled={busy}
          title="Xoá"
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

interface SecretRevealProps {
  reveal: WebhookSecretReveal;
  onClose: () => void;
  onCopy: (text: string, label: string) => void;
}

function SecretRevealDialog({
  reveal,
  onClose,
  onCopy,
}: SecretRevealProps): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg rounded-xl border bg-popover shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b px-4 py-3 bg-amber-50 dark:bg-amber-950/40">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <AlertTriangle className="size-4 text-amber-600" />
            HMAC Secret — Save ngay (chỉ hiển thị 1 lần)
          </h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase font-semibold text-muted-foreground mb-1 block">
              Webhook URL
            </label>
            <div className="flex items-center gap-1">
              <code className="flex-1 bg-muted px-2 py-1.5 rounded text-xs break-all">
                {reveal.webhook_url}
              </code>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onCopy(reveal.webhook_url, "URL")}
              >
                <Copy className="size-4" />
              </Button>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-muted-foreground mb-1 block">
              HMAC Secret
            </label>
            <div className="flex items-center gap-1">
              <code className="flex-1 bg-muted px-2 py-1.5 rounded text-xs break-all">
                {reveal.secret}
              </code>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onCopy(reveal.secret, "Secret")}
              >
                <Copy className="size-4" />
              </Button>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-muted-foreground mb-1 block">
              Sample curl (test)
            </label>
            <div className="flex items-start gap-1">
              <pre className="flex-1 bg-muted px-2 py-1.5 rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
                {reveal.sample_curl}
              </pre>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onCopy(reveal.sample_curl, "curl")}
              >
                <Copy className="size-4" />
              </Button>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground italic">
            Secret được mã hoá lưu DB. Sau khi đóng dialog này, không thể xem
            lại — phải Regenerate nếu mất.
          </p>
        </div>
        <footer className="border-t px-4 py-3 flex justify-end">
          <Button variant="brand" onClick={onClose}>
            Đã lưu, đóng
          </Button>
        </footer>
      </div>
    </div>
  );
}
